import { create } from "zustand";
import { SampleMaturity } from "@/backend/models/sampleMaturity";
import { SampleMaturityFrontendController } from "../controllers/sampleMaturityController";
import { CreateSampleMaturityRequest, UpdateSampleMaturityRequest } from "@/types/sampleMaturityTypes";

const initialState: { sampleMaturities: SampleMaturity[] } = {
  sampleMaturities: [],
};

export interface SampleMaturityStore {
  sampleMaturities: SampleMaturity[];
  setSampleMaturities: (sampleMaturities: SampleMaturity[]) => void;
  fetchSampleMaturitiesByProjectId: (projectId: string) => Promise<void>;
  createSampleMaturity: (data: CreateSampleMaturityRequest) => Promise<void>;
  updateSampleMaturity: (id: string, data: UpdateSampleMaturityRequest) => Promise<void>;
  deleteSampleMaturity: (id: string) => Promise<boolean>;
  reset: () => void;
}

export const useSampleMaturityStore = create<SampleMaturityStore>((set) => ({
  ...initialState,

  setSampleMaturities: (sampleMaturities: SampleMaturity[]) => set({ sampleMaturities }),

  fetchSampleMaturitiesByProjectId: async (projectId: string) => {
    const controller = new SampleMaturityFrontendController();
    const response = await controller.readAllByProjectId(projectId);
    if (response.status === 200) {
      useSampleMaturityStore.getState().setSampleMaturities(response.sampleMaturities.map((m) => new SampleMaturity(m)));
    }
  },

  createSampleMaturity: async (data: CreateSampleMaturityRequest) => {
    const controller = new SampleMaturityFrontendController();
    const response = await controller.create(data);

    if (response.status === 201 && response.sampleMaturity) {
      const sampleMaturity = new SampleMaturity(response.sampleMaturity);
      const current = useSampleMaturityStore.getState().sampleMaturities;
      useSampleMaturityStore.getState().setSampleMaturities([...current, sampleMaturity]);
    }
  },

  updateSampleMaturity: async (id: string, data: UpdateSampleMaturityRequest) => {
    const controller = new SampleMaturityFrontendController();
    const response = await controller.update(id, data);

    if (response.status === 200 && response.sampleMaturity) {
      const sampleMaturity = new SampleMaturity(response.sampleMaturity);
      const current = useSampleMaturityStore.getState().sampleMaturities;
      useSampleMaturityStore.getState().setSampleMaturities(current.map((m) => m.id === id ? sampleMaturity : m));
    }
  },

  deleteSampleMaturity: async (id: string) => {
    const controller = new SampleMaturityFrontendController();
    const response = await controller.delete(id);

    if (response.status === 200) {
      const current = useSampleMaturityStore.getState().sampleMaturities;
      useSampleMaturityStore.getState().setSampleMaturities(current.filter((m) => m.id !== id));
      return true;
    }
    return false;
  },

  reset: () => set({ ...initialState }),
}));
